import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Package, Globe, EyeOff, Save, Search, Pencil, X, Trash2, Download, Barcode } from "lucide-react";
import { Link } from "react-router-dom";
import api, { inr } from "../lib/api";
import ImageUpload from "../components/ImageUpload";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "Free Size"];
const EMPTY = {
  name: "", category: "", occasion: "", price: "", purchase_price: "",
  sizes: [], sku_size: "", stock: 10, primary_image: "", publish: true,
  supplier_name: "", supplier_phone: "", supplier_notes: "", color_variants: [],
};

export default function PosStock() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [occs, setOccs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // null = adding new; else product being edited
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [customSize, setCustomSize] = useState("");
  const [edits, setEdits] = useState({}); // {id: stockValue}

  const load = () => api.get("/admin/products").then((r) => setProducts(Array.isArray(r.data) ? r.data : (r.data.items || [])));
  const loadSuppliers = () => api.get("/admin/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
  const loadCats = () => api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    loadSuppliers();
    loadCats();
    api.get("/occasions").then((r) => setOccs(r.data || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const toggleSize = (s) => setForm((f) => {
    const on = f.sizes.includes(s);
    const sizes = on ? f.sizes.filter((x) => x !== s) : [...f.sizes, s];
    let sku_size = f.sku_size;
    if (!on) sku_size = s;                          // just selected -> auto-pick for the SKU
    else if (f.sku_size === s) sku_size = sizes[0] || ""; // deselected the current SKU size
    return { ...f, sizes, sku_size };
  });
  const addCustomSize = () => {
    const s = customSize.trim();
    if (!s) return;
    setForm((f) => ({ ...f, sizes: f.sizes.includes(s) ? f.sizes : [...f.sizes, s], sku_size: s }));
    setCustomSize("");
  };
  const addColor = () => setForm((f) => ({ ...f, color_variants: [...(f.color_variants || []), { color: "", stock: 0, images: [] }] }));
  const updateColor = (i, patch) => setForm((f) => ({ ...f, color_variants: f.color_variants.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const removeColor = (i) => setForm((f) => ({ ...f, color_variants: f.color_variants.filter((_, idx) => idx !== i) }));

  // When the typed supplier name matches a saved supplier, auto-fill its phone & notes.
  const onSupplierName = (e) => {
    const name = e.target.value;
    const match = suppliers.find((s) => (s.name || "").toLowerCase() === name.trim().toLowerCase());
    setForm((f) => ({
      ...f,
      supplier_name: name,
      ...(match ? { supplier_phone: match.phone || "", supplier_notes: match.notes || "" } : {}),
    }));
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (q && !((p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q))) return false;
      const units = Number(p.stock ?? 0);
      if (stockFilter === "available" && units <= 0) return false;
      if (stockFilter === "out" && units > 0) return false;
      return true;
    });
  }, [products, query, stockFilter]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY); };

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      category: (cats.find((c) => (c.slug || c.name) === p.category)?.name) || p.category || "",
      occasion: p.occasion || "",
      price: p.price ?? "",
      purchase_price: p.purchase_price ?? "",
      sizes: p.sizes || [],
      sku_size: (() => { const m = (p.sku || "").match(/^AMB-\d+-(.+)$/i); return m ? m[1] : ""; })(),
      stock: p.stock ?? 0,
      primary_image: p.primary_image || "",
      publish: !p.offline_only,
      supplier_name: p.supplier_name || "",
      supplier_phone: p.supplier_phone || "",
      supplier_notes: p.supplier_notes || "",
      color_variants: (p.color_variants || []).map((c) => ({ color: c.color || "", stock: c.stock ?? 0, images: c.images || [] })),
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveProduct = async () => {
    if (!form.name.trim()) return toast.error("Enter a product name");
    if (!form.category.trim()) return toast.error("Pick or type a category");
    if (!Number(form.price)) return toast.error("Enter a price");
    if (!form.primary_image) return toast.error("Upload a product image");
    setSaving(true);
    try {
      const base = {
        name: form.name.trim(),
        category: form.category,
        occasion: form.occasion || (occs[0]?.name || "Festive"),
        price: Number(form.price),
        purchase_price: form.purchase_price !== "" ? Number(form.purchase_price) : null,
        sizes: form.sizes,
        stock: Number(form.stock) || 0,
        sku_size: form.sku_size.trim(),
        offline_only: !form.publish,
        supplier_name: form.supplier_name.trim(),
        supplier_phone: form.supplier_phone.trim(),
        supplier_notes: form.supplier_notes.trim(),
        color_variants: (form.color_variants || [])
          .filter((c) => (c.color || "").trim())
          .map((c) => ({ color: c.color.trim(), stock: Number(c.stock) || 0, images: c.images || [] })),
      };
      if (editing) {
        // Preserve original multi-image/hover unless the primary image was changed.
        const imgChanged = form.primary_image !== editing.primary_image;
        const imgFields = imgChanged
          ? { primary_image: form.primary_image, hover_image: form.primary_image, images: [form.primary_image] }
          : { primary_image: editing.primary_image, hover_image: editing.hover_image, images: editing.images };
        await api.put(`/admin/products/${editing.id}`, { ...editing, ...base, ...imgFields });
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", {
          ...base,
          primary_image: form.primary_image,
          hover_image: form.primary_image,
          images: [form.primary_image],
        });
        toast.success(form.publish ? "Product added & published to website" : "Product added (POS only)");
      }
      setForm(EMPTY);
      setShowForm(false);
      setEditing(null);
      load();
      loadSuppliers();
      loadCats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save product");
    } finally { setSaving(false); }
  };

  const saveStock = async (p) => {
    const val = edits[p.id];
    if (val === undefined) return;
    try {
      await api.put(`/admin/products/${p.id}`, { ...p, stock: Number(val) || 0, stock_by_size: null });
      toast.success(`Stock updated for ${p.name}`);
      setEdits((e) => { const c = { ...e }; delete c[p.id]; return c; });
      load();
    } catch { toast.error("Could not update stock"); }
  };

  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, { ...p, offline_only: !p.offline_only });
      toast.success(!p.offline_only ? "Hidden from website (POS only)" : "Published to website");
      load();
    } catch { toast.error("Could not update"); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?\nThis removes it from the POS and the website. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success(`Deleted ${p.name}`);
      load();
    } catch { toast.error("Could not delete product"); }
  };

  const exportCsv = () => {
    if (!products.length) return toast.error("No products to export");
    const header = ["Name", "SKU", "Category", "Selling Price", "Purchase Price", "Stock", "Sizes", "Supplier", "Supplier Phone", "On Website"];
    const rows = [header, ...products.map((p) => [
      p.name, p.sku, p.category, p.price ?? "", p.purchase_price ?? "", p.stock ?? "",
      (p.sizes || []).join("|"), p.supplier_name || "", p.supplier_phone || "", p.offline_only ? "No" : "Yes",
    ])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ambrai-stock-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${products.length} products`);
  };

  return (
    <div className="p-4 sm:p-8" data-testid="pos-stock-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Stock</h1>
          <p className="text-sm text-slate-500">Add products & update stock. Choose whether each piece shows on the website.</p>
        </div>
        <div className="flex items-center gap-2 w-fit">
          <button onClick={exportCsv} className="flex items-center gap-2 px-4 py-2.5 rounded border border-slate-700 text-slate-200 hover:border-amber-500 hover:text-amber-400 font-medium text-sm" data-testid="stock-export">
            <Download size={16} /> Export
          </button>
          <button onClick={() => (showForm ? closeForm() : openAdd())} className="flex items-center gap-2 px-4 py-2.5 rounded bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm" data-testid="stock-add-toggle">
            {showForm ? <><X size={16} /> Close</> : <><Plus size={16} /> Add product</>}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 mb-8" data-testid="stock-add-form">
          <div className="flex items-center gap-2 mb-4">
            {editing ? <Pencil size={16} className="text-amber-400" /> : <Plus size={16} className="text-amber-400" />}
            <h2 className="text-slate-100 font-semibold" data-testid="stock-form-title">{editing ? `Edit — ${editing.name}` : "Add new product"}</h2>
            {editing && <span className="text-xs text-slate-500 font-mono">{editing.sku}</span>}
          </div>
          <div className="grid lg:grid-cols-[220px_1fr] gap-6">
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-widest block mb-2">Product image</label>
            <ImageUpload value={form.primary_image} onChange={(url) => setForm((f) => ({ ...f, primary_image: url }))} />
          </div>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name">
                <input value={form.name} onChange={set("name")} className={inp} data-testid="stock-name" placeholder="e.g. Ivory Bloom Anarkali" />
              </Field>
              <Field label="Category">
                <input
                  value={form.category}
                  onChange={set("category")}
                  list="cat-suggestions"
                  className={inp}
                  data-testid="stock-category"
                  placeholder="Pick or type a new category"
                  autoComplete="off"
                />
                <datalist id="cat-suggestions">
                  {cats.map((c) => <option key={c.id || c.slug} value={c.name} />)}
                </datalist>
              </Field>
              <Field label="Occasion">
                <select value={form.occasion} onChange={set("occasion")} className={inp}>
                  <option value="">Default</option>
                  {occs.map((o) => <option key={o.id || o.name} value={o.name}>{o.name}</option>)}
                </select>
              </Field>
              <Field label="Sizes">
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {[...SIZES, ...form.sizes.filter((s) => !SIZES.includes(s))].map((s) => (
                    <button key={s} type="button" onClick={() => toggleSize(s)} className={`text-xs px-2.5 py-1 rounded border ${form.sizes.includes(s) ? "bg-amber-500 border-amber-500 text-slate-950 font-semibold" : "border-slate-700 text-slate-400"}`} data-testid={`stock-size-${s}`}>{s}</button>
                  ))}
                </div>
                <div className="flex gap-1.5 mt-2">
                  <input
                    value={customSize}
                    onChange={(e) => setCustomSize(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomSize(); } }}
                    className="flex-1 bg-slate-950 border border-slate-800 rounded px-2.5 py-1.5 text-xs text-slate-200 outline-none focus:border-amber-500"
                    placeholder="Add custom size (e.g. 38, Kids-M)"
                    data-testid="stock-custom-size"
                    autoComplete="off"
                  />
                  <button type="button" onClick={addCustomSize} className="text-xs px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="stock-custom-size-add">Add</button>
                </div>
              </Field>
              <Field label="Selling price (₹)">
                <input type="number" value={form.price} onChange={set("price")} className={inp} data-testid="stock-price" />
              </Field>
              <Field label="Purchase price / Cost (₹)">
                <input type="number" value={form.purchase_price} onChange={set("purchase_price")} className={inp} placeholder="optional" />
              </Field>
              <Field label="Stock quantity">
                <input type="number" value={form.stock} onChange={set("stock")} className={inp} data-testid="stock-qty" />
              </Field>
              {!editing && (
              <Field label="Size (for SKU)">
                <input
                  value={form.sku_size}
                  onChange={set("sku_size")}
                  list="sku-size-suggestions"
                  className={inp}
                  data-testid="stock-sku-size"
                  placeholder="Pick or type a custom size"
                  autoComplete="off"
                />
                <datalist id="sku-size-suggestions">
                  {SIZES.map((s) => <option key={s} value={s} />)}
                </datalist>
                <span className="text-[11px] text-slate-500 mt-1 block">Auto-filled from the size you pick above — edit if you want a different one. Goes into the SKU, e.g. AMB-8-{form.sku_size ? form.sku_size.toUpperCase() : "M"}. Leave blank for no size.</span>
              </Field>
              )}
              {editing && (
              <Field label="Size (in SKU)">
                <input
                  value={form.sku_size}
                  onChange={set("sku_size")}
                  list="sku-size-suggestions-edit"
                  className={inp}
                  data-testid="stock-sku-size"
                  placeholder="Pick or type a custom size"
                  autoComplete="off"
                />
                <datalist id="sku-size-suggestions-edit">
                  {SIZES.map((s) => <option key={s} value={s} />)}
                </datalist>
                <span className="text-[11px] text-slate-500 mt-1 block">This is the size stored in the SKU. Changing it updates the SKU to AMB-N-{form.sku_size ? form.sku_size.toUpperCase() : "(none)"} (the product number stays the same).</span>
              </Field>
              )}
              <Field label="Supplier">
                <input
                  value={form.supplier_name}
                  onChange={onSupplierName}
                  list="supplier-suggestions"
                  className={inp}
                  data-testid="stock-supplier-name"
                  placeholder="Start typing — saved suppliers suggest"
                  autoComplete="off"
                />
                <datalist id="supplier-suggestions">
                  {suppliers.map((s) => <option key={s.name} value={s.name}>{s.phone ? `${s.phone}` : ""}</option>)}
                </datalist>
              </Field>
              <Field label="Supplier phone">
                <input value={form.supplier_phone} onChange={set("supplier_phone")} className={inp} data-testid="stock-supplier-phone" placeholder="optional" autoComplete="off" />
              </Field>
              <Field label="Supplier notes">
                <input value={form.supplier_notes} onChange={set("supplier_notes")} className={inp} data-testid="stock-supplier-notes" placeholder="e.g. GST no., address (optional)" autoComplete="off" />
              </Field>
            </div>
            <label className="flex items-center gap-2.5 text-sm text-slate-200 cursor-pointer select-none bg-slate-950 border border-slate-800 rounded px-3 py-2.5 w-fit" data-testid="stock-publish-toggle">
              <input type="checkbox" checked={form.publish} onChange={(e) => setForm((f) => ({ ...f, publish: e.target.checked }))} className="accent-amber-500 h-4 w-4" />
              <Globe size={15} className="text-amber-400" /> Show this product on the website
              <span className="text-xs text-slate-500">(uncheck for POS-only)</span>
            </label>

            <div className="border-t border-slate-800 pt-4" data-testid="stock-colors-section">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-slate-400 uppercase tracking-widest">Colours (optional)</p>
                <button type="button" onClick={addColor} className="text-xs px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-amber-400 border border-slate-700 flex items-center gap-1.5" data-testid="stock-color-add"><Plus size={13} /> Add colour</button>
              </div>
              <p className="text-[11px] text-slate-500 mb-3">Add colours to track stock &amp; a barcode per colour. Each colour can have its own photo (leave blank to reuse the main image). Total stock = sum of colours.</p>
              {(form.color_variants || []).length === 0 && <p className="text-xs text-slate-600">No colours — this is a single-colour product.</p>}
              <div className="space-y-2">
                {(form.color_variants || []).map((c, i) => (
                  <div key={i} className="flex flex-wrap items-end gap-2 bg-slate-950 border border-slate-800 rounded p-2.5" data-testid={`stock-color-row-${i}`}>
                    <div className="w-20">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-1">Photo</span>
                      <ImageUpload value={(c.images && c.images[0]) || ""} onChange={(url) => updateColor(i, { images: url ? [url] : [] })} />
                    </div>
                    <label className="flex-1 min-w-[120px]">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-1">Colour name</span>
                      <input value={c.color} onChange={(e) => updateColor(i, { color: e.target.value })} className={inp} placeholder="e.g. Red" data-testid={`stock-color-name-${i}`} autoComplete="off" />
                    </label>
                    <label className="w-24">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-1">Stock</span>
                      <input type="number" value={c.stock} onChange={(e) => updateColor(i, { stock: e.target.value })} className={inp} data-testid={`stock-color-stock-${i}`} />
                    </label>
                    <button type="button" onClick={() => removeColor(i)} className="p-2 rounded border border-slate-700 text-slate-400 hover:border-red-500 hover:text-red-400" title="Remove colour" data-testid={`stock-color-remove-${i}`}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={saveProduct} disabled={saving} className="px-5 py-2.5 rounded bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm disabled:opacity-50" data-testid="stock-save">
              {saving ? "Saving…" : (editing ? "Update product" : "Save product")}
            </button>
          </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products by name or SKU" className="w-full bg-slate-900 border border-slate-800 rounded pl-9 pr-3 py-2.5 text-sm text-slate-200" data-testid="stock-search" />
        </div>
        <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className="bg-slate-900 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 sm:w-48" data-testid="stock-availability-filter">
          <option value="all">All products</option>
          <option value="available">Available (in stock)</option>
          <option value="out">Out of stock</option>
        </select>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800" data-testid="stock-list">
        {filtered.length === 0 && <p className="p-8 text-center text-slate-500 text-sm">No products found.</p>}
        {filtered.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`stock-row-${p.id}`}>
            <img src={p.primary_image} alt="" className="h-12 w-10 object-cover rounded bg-slate-800 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-slate-100 text-sm truncate">{p.name}</p>
              <p className="text-xs text-slate-500 font-mono">{p.sku} · {inr(p.price)}</p>
              {p.supplier_name && (
                <p className="text-[11px] text-slate-500 truncate" data-testid={`stock-supplier-${p.id}`}>
                  Supplier: <span className="text-slate-400">{p.supplier_name}</span>{p.supplier_phone ? ` · ${p.supplier_phone}` : ""}
                </p>
              )}
              {(p.color_variants || []).length > 0 && (
                <p className="text-[11px] text-slate-500 truncate" data-testid={`stock-colors-${p.id}`}>
                  Colours: <span className="text-slate-400">{p.color_variants.map((c) => `${c.color} (${c.stock})`).join(", ")}</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-slate-500 uppercase tracking-widest">Stock</span>
              <input
                type="number"
                value={edits[p.id] !== undefined ? edits[p.id] : (p.stock ?? 0)}
                onChange={(e) => setEdits((ed) => ({ ...ed, [p.id]: e.target.value }))}
                className="w-16 bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm text-slate-200 font-mono text-center"
                data-testid={`stock-input-${p.id}`}
              />
              {edits[p.id] !== undefined && (
                <button onClick={() => saveStock(p)} className="p-1.5 rounded bg-amber-500 text-slate-950" title="Save stock" data-testid={`stock-save-${p.id}`}><Save size={14} /></button>
              )}
            </div>
            <Link
              to={`/pos/barcodes?sku=${encodeURIComponent(p.sku || "")}`}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-slate-700 text-slate-300 hover:border-amber-500 hover:text-amber-400"
              title="Print barcode for this product"
              data-testid={`stock-barcode-${p.id}`}
            >
              <Barcode size={13} /> Barcode
            </Link>
            <button
              onClick={() => startEdit(p)}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-slate-700 text-slate-300 hover:border-amber-500 hover:text-amber-400"
              title="Edit product details"
              data-testid={`stock-edit-${p.id}`}
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              onClick={() => togglePublish(p)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border ${p.offline_only ? "border-slate-700 text-slate-400" : "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"}`}
              title={p.offline_only ? "POS only — click to publish on website" : "Live on website — click to hide"}
              data-testid={`stock-website-${p.id}`}
            >
              {p.offline_only ? <><EyeOff size={13} /> POS only</> : <><Globe size={13} /> On website</>}
            </button>
            <button
              onClick={() => deleteProduct(p)}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-slate-700 text-slate-400 hover:border-red-500 hover:text-red-400"
              title="Delete product"
              data-testid={`stock-delete-${p.id}`}
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const inp = "w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 outline-none focus:border-amber-500";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs text-slate-400 uppercase tracking-widest block mb-1.5">{label}</span>
    {children}
  </label>
);
